import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import {
  UserService,
  UserNameDto,
  VDPConfigurationState,
  BidListFilterService
} from 'remarketing-angular-library';
import { take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TrapFocusComponent } from 'remarketing-ui-components-lib';
import { UserPopoverComponent } from './user-popover/user-popover.component';

@Component({
    selector: 'user-dropdown',
    templateUrl: './bidlist-user-dropdown.component.html',
    styleUrls: ['./bidlist-user-dropdown.component.scss'],
    imports: [TranslocoDirective, PopoverModule, TrapFocusComponent, UserPopoverComponent]
})
export class BidlistUserDropdownComponent implements OnInit, OnDestroy {
  @Input() userUuids: string[] = [];
  listOfUsers: UserNameDto[] = [];
  defaultUser: UserNameDto = { userUuid: 'all', userName: '' };
  private readonly destroyed$ = new Subject();

  constructor(
    private userService: UserService,
    private store: Store,
    readonly bidListFilterService: BidListFilterService,
    private readonly translocoService: TranslocoService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.getTranslations();
    this.userService
      .getUserNamesByUuids({ userUuids: this.userUuids })
      .pipe(take(1))
      .subscribe(res => {
        res.forEach(user => {
          this.listOfUsers.push({ userUuid: user.userUuid, userName: user.userName });
        });
        this.setDefaultUser();
        // A selection reloads the bid list, which recreates this component (fresh `userUuids`
        // input) before this HTTP response resolves. Because that recreation happens inside an
        // OnPush ancestor's non-dirty subtree, Angular never schedules a follow-up check once this
        // async callback updates `defaultUser` here — the toggle label is left showing its initial
        // default text until some unrelated click elsewhere forces a check. detectChanges() makes
        // the just-resolved, correct label paint immediately instead of waiting on a second click.
        this.changeDetectorRef.detectChanges();
      });
  }

  filterBySelectedUser(user: UserNameDto) {
    const sortedUuid = user.userUuid === 'all' ? null : user.userUuid;
    this.defaultUser = user;
    const newBidListSort = this.bidListFilterService.updateBidListSort({ addedByUuid: sortedUuid });
    this.bidListFilterService.navigateBidListToBidList(newBidListSort);
  }

  setDefaultUser(): void {
    const { bidListSort } = this.store.selectSnapshot(VDPConfigurationState.currentUserState);

    if (bidListSort.addedByUuid) {
      const possibleUser = this.listOfUsers.find(user => user.userUuid === bidListSort.addedByUuid);
      this.defaultUser = possibleUser ? possibleUser : this.defaultUser;
    }
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('vehicles.bidListFilter.bidListUserDropdown')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.defaultUser.userName = translate.defaultAdded;
        this.listOfUsers.splice(0, 1, { userUuid: 'all', userName: translate.dropdownAllUser });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
